import { HelpTextDiv, InputHintDiv, LabelDialogParts, LabelDialogType, LabelHelpInfoPopover, LabelManagementLink, LabelStyles, useSetIsLabelSevere } from '@tableau/catalog-ui-temp';
import { getMaxCharValidation, ToggleSwitchWidget } from '@tableau/widgets';
import React from 'react';

import { CompositeSessionInfoSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';

import { TextFormatterOrAreaWidget } from '../common/TextFormatterOrAreaWidget';
import { useLabelValues } from './LabelDialog';

export type DataQualityTriggerConfigurationUIAction =
  | { type: 'setEnableTrigger', value: DataQualityTriggerConfigurationUIProps['isActive'] }
  | { type: 'setIsSevere', value: DataQualityTriggerConfigurationUIProps['isSevere'] }
  | { type: 'setMessage', value: DataQualityTriggerConfigurationUIProps['message'] };

export type DataQualityTriggerConfigurationUIProps = {
  readonly contentName?: string;
  readonly contentType: 'datasource' | 'flow';
  readonly isActive: boolean;
  readonly isSevere: boolean;
  readonly message: string;
  readonly dispatch: React.Dispatch<DataQualityTriggerConfigurationUIAction>;
  readonly dialogType: LabelDialogType;
}

const TRIGGER_LABEL_DIALOG_TOGGLE_TEST_ID = 'trigger-label-dialog-toggle';
const TRIGGER_LABEL_DIALOG_MESSAGE_TEST_ID = 'trigger-label-message-toggle';


// This is used by the Trigger Uber Dialog. Other trigger dialogs use LabelDialog
export function DataQualityTriggerConfigurationUI({ dispatch, isSevere: initialIsSevere, ...props }: DataQualityTriggerConfigurationUIProps): React.ReactElement {
  const handleMessageChange = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => dispatch({ type: 'setMessage', value: e.currentTarget.value }),
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  const [SevereWarningContent, isSevere] = useSetIsLabelSevere({ disabled: !props.isActive, initial: initialIsSevere, isLabelValue: true });
  React.useEffect(() => {
    if (initialIsSevere !== isSevere) {
      dispatch({ type: 'setIsSevere', value: isSevere });
    }
  }, [dispatch, initialIsSevere, isSevere]);

  const { message, valid } = getMaxCharValidation(props?.message ?? '', LabelStyles.MESSAGE_MAXIMUM_LENGTH);
  const labelValues = useLabelValues(props.dialogType, props.dialogType);
  const labelValue = labelValues.labelValueToDescription.get(props.dialogType);
  const isSiteOrServerAdmin = CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector(getAppStore().getState());
  const labelMgmtEnabled = FeatureFlags.labelManagementEnabled;

  return (
    <>
      {props.contentName ?
        <LabelStyles.ContentItemNameDiv>
          <div style={LabelStyles.iconStyle}>
            <LabelDialogParts.ContentTypeIcon {...props} />
          </div>
          {props.contentName}
        </LabelStyles.ContentItemNameDiv>
        : null}

      <LabelHelpInfoPopover dialogType={props.dialogType} />

      <ToggleSwitchWidget
        id={TRIGGER_LABEL_DIALOG_TOGGLE_TEST_ID} // used for initial focus
        textAlign={'right'}
        testId={TRIGGER_LABEL_DIALOG_TOGGLE_TEST_ID}
        onChange={(e) => dispatch({ type: 'setEnableTrigger', value: e.target.checked })}
        checked={props.isActive}
        children={LabelDialogParts.getToggleLabel(props.dialogType)}
        style={{ justifyContent: 'flex-start', marginTop: '24px' }}
      />
      <Description disabled={!props.isActive} showLink={isSiteOrServerAdmin && labelMgmtEnabled} description={labelValue} />

      {SevereWarningContent}

      <TextFormatterOrAreaWidget
        label={<InputHintDiv disabled={!props.isActive}>{Messages.dataQualityTriggerMessage()}</InputHintDiv>}
        onChange={handleMessageChange}
        id={TRIGGER_LABEL_DIALOG_MESSAGE_TEST_ID}
        testId={TRIGGER_LABEL_DIALOG_MESSAGE_TEST_ID}
        disabled={!props.isActive}
        valid={valid}
        message={message}
        style={LabelStyles.markupEditorStyle}
        markup={props.message}
      />
    </>
  );
}


export function Description({ disabled, showLink, description }: { disabled: boolean, showLink: boolean, description: string | undefined }) {
  return (
    <HelpTextDiv disabled={disabled}>
      <div style={{ paddingTop: '8px' }}>
        <span> {description ?? ''} </span>
        {showLink && description ?
          <span style={{ margin: '0 3px' }}>
            <LabelManagementLink /></span> : <></>}
      </div>
    </HelpTextDiv>
  );
}
